import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import { observer } from 'mobx-react'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'
import Loading from 'modules/core/components/Loading'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMemo } from 'modules/core/hooks/useMemo'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'

import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import { usePolicyAssignmentEligibleEmployees } from 'modules/payroll_bundle/hooks/usePolicyAssignmentEligibleEmployees'

interface Props {
  onChange: (value: number[]) => void
  country: string
  currency: string
  selectedEmployeeIds: number[]
  disabledIneligibleEmployees?: boolean
  filterProps?: {
    hideFilterByStatus?: boolean
    showFilterByTeam?: boolean
    showFilterByLegalEntity?: boolean
  }
  contractVersions: ContractVersions
}

const EmployeesSelector = ({
  onChange,
  country,
  currency,
  selectedEmployeeIds,
  filterProps: _filterProps,
  contractVersions,
}: Props) => {
  const [activeEmployeesList, setActiveEmployeesList] = React.useState<Employee[]>([])

  const eligibleEmployeeIds: number[] = useMemo(() => {
    const eligibleEmployees = employees.filter((employee) => {
      const isActiveOnPolicy = !employee.isTerminated

      const legalEntity = employeeLegalEntity(employee)
      const matchesCurrencyAndCountry =
        legalEntity.attributes.currency === currency && legalEntity.attributes.country === country

      if (currency !== null && country !== null) {
        return isActiveOnPolicy && matchesCurrencyAndCountry
      } else {
        return isActiveOnPolicy
      }
    })
    return eligibleEmployees.map((employee) => employee.id)
  }, [country, currency])

  const { fetching, policyAssignmentEligibleEmployeeIds } =
    usePolicyAssignmentEligibleEmployees(eligibleEmployeeIds)

  const ineligibleEmployeeIds: number[] = policyAssignmentEligibleEmployeeIds
    ? eligibleEmployeeIds.filter(
        (employeeId) => !policyAssignmentEligibleEmployeeIds.includes(employeeId)
      )
    : eligibleEmployeeIds

  useUnsafeEffect(() => {
    const today = moment()

    const activePolicyContracts = contractVersions.filter((contract) =>
      contract.activeWithinDateRange(today, today)
    )

    const policyEmployeeIdsWithActiveContract = new Set(
      activePolicyContracts.map((contract) => contract.attributes.employee_id)
    )

    const activeEmployees = employees.filter((employee) => {
      const employeeHasActiveContract =
        policyEmployeeIdsWithActiveContract.has(employee.id) &&
        employee.activeWithinDateRange(today, today)

      const legalEntity = employeeLegalEntity(employee)
      const employeeHasCorrectCurrencyAndCountry =
        country === legalEntity.country && currency === legalEntity.currency

      return employeeHasCorrectCurrencyAndCountry && employeeHasActiveContract
    })

    setActiveEmployeesList(activeEmployees)
  }, [country, currency])

  const dimEmployeeSelector = !country.length || !policyAssignmentEligibleEmployeeIds
  const activeEmployeeIds = activeEmployeesList.map((e) => e.id)

  const selectableEmployeeIds = React.useMemo(
    () => activeEmployeeIds.filter((id) => !ineligibleEmployeeIds.includes(id)),
    [activeEmployeeIds, ineligibleEmployeeIds]
  )

  return (
    <Box position="relative">
      <Box
        style={{
          opacity: dimEmployeeSelector ? '0.5 ' : '1',
        }}
      >
        <EmployeeSelectorV2
          multiple
          value={selectedEmployeeIds ?? []}
          onChange={(state) => {
            if (state.type === 'manual') {
              onChange(state.ids)
            } else if (state.type === 'all') {
              onChange(selectableEmployeeIds.filter((id) => !state.excludedIds.includes(id)))
            }
          }}
          forcedFilters={{
            employeeIds: activeEmployeeIds.length > 0 ? activeEmployeeIds : undefined,
            excludeIds: ineligibleEmployeeIds,
          }}
        />
      </Box>
      {fetching && (
        <Box
          height="full"
          width="full"
          position="absolute"
          justifyContent="center"
          alignItems="center"
        >
          <Loading />
        </Box>
      )}
    </Box>
  )
}

export default observer(EmployeesSelector)
